import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { object, string } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { neutral } from '../../../styles/mui/colors';
import getFilterIcon from '../../../util/getFilterIcon';

/**
 * @param {object} props - Component props
 * @param {Array<{key: string, label: string}>} props.options - Array of objects with keys: { key: string, label: string, icon: string}
 * @param {Array<string>} props.queryParamNames - Array of query param names
 * @param {Function} props.onSelect - Function to call when a filter is selected. Takes an object with query param names as keys and values as values.
 * @param {object} props.initialValues - Object with initial values for query params. Keys are query param names, values are values.
 * @returns {React.ReactNode} - Component
 */
const SelectSingleFilterPlain = ({ options, queryParamNames, onSelect, initialValues = {} }) => {
  // label is in here but not necessary anymore
  const [selectedOptions, setSelectedOptions] = useState({});
  const intl = useIntl();

  useEffect(() => {
    const value = initialValues[queryParamNames[0]];
    let initialSelectedOptions = {};

    if (Array.isArray(value)) {
      initialSelectedOptions = value.reduce((acc, val) => {
        acc[val] = true;
        return acc;
      }, {});
    } else if (typeof value === 'string') {
      // Remove any prefixes like 'has_any:' or 'has_all:'
      const valueWithoutPrefix = value.includes(':') ? value.split(':')[1] : value;
      const values = valueWithoutPrefix.split(',');
      initialSelectedOptions = values.reduce((acc, val) => {
        acc[val] = true;
        return acc;
      }, {});
    }
    setSelectedOptions(initialSelectedOptions);
  }, [initialValues, queryParamNames]);

  const handleChange = (option) => {
    const newSelections = {
      ...selectedOptions,
      [option.key]: !selectedOptions[option.key],
    };
    setSelectedOptions(newSelections);

    const selectedKeys = Object.keys(newSelections).filter((key) => newSelections[key]);

    // Format the value as a comma-separated string
    let valueToSend = null;
    if (selectedKeys.length > 0) {
      valueToSend = selectedKeys.join(',');
    }

    onSelect({ [queryParamNames[0]]: valueToSend });
  };

  return (
    <Box>
      {options.map((option) => (
        <Box
          border={`1px solid ${neutral[100]}`}
          borderRadius={1}
          key={option.key}
          mt={1}
          px={2}
          py={1}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={!!selectedOptions[option.key]}
                color="primary"
                onChange={() => handleChange(option)}
              />
            }
            label={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                {option?.icon ? getFilterIcon(option.icon) : null}
                <Typography sx={{ ml: 2 }} variant="body1">
                  {intl.formatMessage({ id: option.label })}
                </Typography>
              </Box>
            }
            labelPlacement="start"
            sx={{ margin: 0, width: '100%', justifyContent: 'space-between' }}
          />
        </Box>
      ))}
    </Box>
  );
};

export default SelectSingleFilterPlain;
